import { memo, useMemo } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import I18n from 'i18n-js';

import useCollectionRankingTimer from '@lootboy/lib/slices/cardCollections/hooks/useCollectionRankingTimer';
import { getTimeFormatUnicode } from '@lootboy/lib/utils/dateAndTime';

import { CircleLoader } from '~/components/atoms/CircleLoader';

import styles from './RankingTimer.module.scss';

function RankingTimer({
  className,
  collectionId,
}: {
  className?: string;
  collectionId: string;
}): JSX.Element {
  const rankingTimer = useCollectionRankingTimer(collectionId);

  const lastUpdate = useMemo(() => {
    if (!rankingTimer?.lastUpdatedTimestamp) return '';
    return format(
      new Date(rankingTimer.lastUpdatedTimestamp),
      getTimeFormatUnicode(I18n.locale),
    );
  }, [rankingTimer?.lastUpdatedTimestamp]);

  if (rankingTimer === undefined) {
    return <></>;
  }

  const {
    timer: { ended: timerEnded, seconds, hours, minutes },
    isCollectionEnded,
  } = rankingTimer;

  if (isCollectionEnded) {
    return <></>;
  }

  if (timerEnded) {
    return (
      <div className={styles.loaderContainer}>
        <CircleLoader className={styles.loader} />
      </div>
    );
  }

  return (
    <p className={classNames(styles.timer, className)}>
      {minutes < 1
        ? I18n.t('collections_ranking_sec_bs_sub', {
            lastUpdate,
            s: seconds,
          })
        : I18n.t('collections_ranking_bs_sub', {
            lastUpdate,
            h: hours,
            m: minutes,
          })}
    </p>
  );
}

export default memo(RankingTimer);
